import type { SeoMeta } from '@dma/types';
import { InvalidProjectError } from '../errors/InvalidProjectError';

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export type ProjectStatus = 'licensed' | 'exploration' | 'active' | 'completed' | 'planned';
const VALID_STATUSES: ProjectStatus[] = ['licensed', 'exploration', 'active', 'completed', 'planned'];

export interface Coordinates {
  lat: number;
  lng: number;
}

export interface TechnicalSpecification {
  label: string;
  value: string;
}

export interface ProjectInput {
  id: string;
  slug: string;
  name: string;
  status: ProjectStatus;
  /** null when the exact province is not yet on record — never guessed. */
  province: string | null;
  /** null when the exact city/county is not yet on record — never guessed. */
  city: string | null;
  /** null when GPS coordinates are not yet on record — never guessed. */
  coordinates: Coordinates | null;
  /** Empty when the target mineral has not been confirmed yet for this area. */
  minerals: string[];
  coverImage: string;
  gallery: string[];
  description: string;
  technicalSpecifications: TechnicalSpecification[];
  companyRole: string;
  /** null when the exact start date is not yet on record — never guessed. */
  startDate: string | null;
  completionDate: string | null;
  seo: SeoMeta;
}

/**
 * Entity per the pattern in features/services/domain/entities/Service.entity.ts.
 * Real business rules beyond simple non-emptiness: geographic coordinates
 * must be within valid ranges (when known), and completionDate (if set)
 * cannot precede startDate (when both are known) — both are the kind of
 * data-integrity mistake a CMS editor could make that the UI should never
 * silently accept.
 *
 * `province`/`city`/`coordinates`/`startDate`/`minerals` are nullable/emptiable
 * rather than required, unlike the original version of this entity — several
 * real projects (see content/projects.content.ts) have confirmed names and
 * status but not yet a confirmed location, start date, or target mineral.
 * Fabricating those to satisfy a "required" rule would put false facts on a
 * real company's public site, which is worse than an honest "نامشخص".
 */
export class Project {
  private constructor(public readonly props: ProjectInput) {
    Object.freeze(this.props);
    Object.freeze(this);
  }

  public static create(input: ProjectInput): Project {
    const name = input.name.trim();
    const slug = input.slug.trim();
    const province = input.province?.trim() || null;
    const city = input.city?.trim() || null;

    if (name.length < 2 || name.length > 150) {
      throw new InvalidProjectError('name must be between 2 and 150 characters.');
    }
    if (!SLUG_PATTERN.test(slug)) {
      throw new InvalidProjectError(`slug "${slug}" must be lowercase kebab-case.`);
    }
    if (!VALID_STATUSES.includes(input.status)) {
      throw new InvalidProjectError(`status must be one of: ${VALID_STATUSES.join(', ')}.`);
    }
    if (input.coordinates !== null) {
      if (input.coordinates.lat < -90 || input.coordinates.lat > 90) {
        throw new InvalidProjectError('coordinates.lat must be between -90 and 90.');
      }
      if (input.coordinates.lng < -180 || input.coordinates.lng > 180) {
        throw new InvalidProjectError('coordinates.lng must be between -180 and 180.');
      }
    }
    if (input.coverImage.trim().length === 0) {
      throw new InvalidProjectError('coverImage must not be empty.');
    }
    if (input.startDate !== null && Number.isNaN(Date.parse(input.startDate))) {
      throw new InvalidProjectError(`startDate "${input.startDate}" is not a valid date.`);
    }
    if (input.completionDate !== null) {
      if (Number.isNaN(Date.parse(input.completionDate))) {
        throw new InvalidProjectError(`completionDate "${input.completionDate}" is not a valid date.`);
      }
      if (input.startDate !== null && Date.parse(input.completionDate) < Date.parse(input.startDate)) {
        throw new InvalidProjectError('completionDate must not be earlier than startDate.');
      }
    }

    return new Project({ ...input, name, slug, province, city });
  }
}
